import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { DateKv } from "@/components/ui/date-kv";
import { Input } from "@/components/ui/input";
import { Kv } from "@/components/ui/kv";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { T } from "@/i18n";
import {
  CreateHotelRoom,
  HotelRoomType,
  HotelRoomTypesAsArray,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export function Form({
  model,
  onUpsert,
  hotelId,
}: {
  hotelId: string;
  model?: CreateHotelRoom;
  onUpsert: (data: CreateHotelRoom) => Promise<void>;
}) {
  const [selectedType, setSelectedType] = useState<HotelRoomType | null>(null);
  const { t } = useTranslation();

  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateHotelRoom>>(() => {
    const config: FormOptions<CreateHotelRoom> = {
      year: {
        value: model?.year ?? new Date().getFullYear(),
      },
      type: {
        value: model?.type,
      },
      count: {
        value: model?.count,
      },
      hotelId: {
        value: parseInt(hotelId),
      },
      designation: {
        value: model?.designation,
      },
      extraBeds: {
        value: model?.extraBeds,
      },
      averageExtraBedRateMap: {
        value: model?.averageExtraBedRateMap,
      },
      notes: {
        value: model?.notes,
      },
      pricing: {
        value: model?.pricing,
      },
      earlyBookingDiscountDays: {
        value: model?.earlyBookingDiscountDays,
      },
      bookingDiscountMap: {
        value: model?.bookingDiscountMap ?? {},
      },
    };
    return config;
  }, [model, hotelId]);

  return (
    <DomainUpsert
      title={model?.designation ?? t("hotelRooms.new.title")}
      buttonTranslation={
        isEdit ? "hotelRooms.update.button" : "hotelRooms.new.button"
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "designation",
          label: "hotelRooms.fields.designation",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.designation ?? ""}
              onChange={(e) => {
                form.setValue("designation", e.target.value);
              }}
            />
          ),
        },
        {
          key: "count",
          label: "hotelRooms.fields.count",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              min="0"
              value={form.value.count ?? ""}
              onChange={(e) => {
                form.setValue("count", parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          key: "extraBeds",
          label: "hotelRooms.fields.extraBeds",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              value={form.value.extraBeds ?? ""}
              onChange={(e) => {
                form.setValue("extraBeds", parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          key: "averageExtraBedRateMap",
          label: "hotelRooms.fields.averageExtraBedRateMap",
          input: (form, id) => (
            <Kv
              className="w-full"
              id={id}
              onChange={(v) => {
                form.setValue("averageExtraBedRateMap", v);
              }}
              value={form.value.averageExtraBedRateMap ?? {}}
            />
          ),
        },
        {
          key: "earlyBookingDiscountDays",
          label: "hotelRooms.fields.earlyBookingDiscountDays",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              value={form.value.earlyBookingDiscountDays ?? ""}
              onChange={(e) => {
                form.setValue(
                  "earlyBookingDiscountDays",
                  parseInt(e.target.value),
                );
              }}
            />
          ),
        },
        {
          key: "bookingDiscountMap",
          label: "hotelRooms.fields.bookingDiscountMap",
          input: (form, id) => (
            <DateKv
              className="w-full"
              id={id}
              value={form.value.bookingDiscountMap ?? {}}
              onChange={(v) => {
                form.setValue("bookingDiscountMap", v);
              }}
            />
          ),
        },
        {
          key: "notes",
          label: "hotelRooms.fields.notes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.notes ?? ""}
              onChange={(e) => {
                form.setValue("notes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "pricing",
          label: "hotelRooms.fields.pricing",
          input: (form, id) => (
            <div className="grid w-full gap-6">
              <div className="flex items-center gap-2">
                <SelectSearch
                  id={id}
                  className="w-full"
                  value={selectedType ?? undefined}
                  onChange={(v) => {
                    setSelectedType(v);
                  }}
                  options={
                    HotelRoomTypesAsArray?.filter(
                      (v) =>
                        !form.value.pricing?.some((p) => p.type === v) ||
                        v === HotelRoomType.Other,
                    ).map((v) => ({
                      value: v,
                      label: t(`hotelRooms.types.${v}`),
                    })) ?? []
                  }
                />
                <Button
                  variant="secondary"
                  type="button"
                  disabled={selectedType === null}
                  onClick={() => {
                    if (selectedType === null) return;
                    form.setValue("pricing", [
                      ...(form.value.pricing ?? []),
                      {
                        type: selectedType,
                      },
                    ]);
                    setSelectedType(null);
                  }}
                >
                  <Plus />
                </Button>
              </div>
              <div className="grid gap-3">
                {form.value.pricing?.map(({ type, notes, prices }, i) => (
                  <div className="grid gap-1" key={`${type}-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-primary">
                        <T tKey={`hotelRooms.types.${type}`} />
                      </span>
                      <Button
                        variant="text-destructive"
                        type="button"
                        onClick={() => {
                          form.setValue(
                            "pricing",
                            form.value.pricing?.filter((_, idx) => idx !== i),
                          );
                        }}
                      >
                        <T tKey={`hotelRooms.deleteType`} />
                      </Button>
                    </div>
                    {type === HotelRoomType.Other && (
                      <Input
                        id="notes"
                        placeholder={t("hotelRooms.fields.roomNotes")}
                        className="mb-2"
                        value={notes ?? undefined}
                        onChange={(e) => {
                          form.setValue(
                            "pricing",
                            form.value.pricing?.map((p, idx) =>
                              idx !== i
                                ? p
                                : {
                                    type,
                                    notes: e.target.value,
                                    prices,
                                  },
                            ),
                          );
                        }}
                      />
                    )}
                    <DateKv
                      sort
                      value={prices ?? {}}
                      onChange={(v) => {
                        form.setValue(
                          "pricing",
                          form.value.pricing?.map((p, idx) =>
                            idx !== i
                              ? p
                              : {
                                  type,
                                  notes,
                                  prices: v,
                                },
                          ),
                        );
                      }}
                    />
                    <Separator />
                  </div>
                ))}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}
